import { Button, Field, SignatureArt } from './ui/controls';
import React, { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const Signup: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const { signup } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    try {
      setLoading(true);
      await signup(email, password);
      toast.success(t('auth.accountCreated'));
      navigate('/sudoku');
    } catch (error) {
      setError(t('auth.failedToCreateAccount'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto grid max-w-6xl items-start gap-12 px-4 py-8 sm:px-6 lg:grid-cols-2 lg:py-16">
      <div className="mx-auto w-full max-w-md space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            {t('auth.signup')}
          </h2>
        </div>

        {error && <p role="alert" className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950 dark:text-red-200">{error}</p>}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email-address" className="mb-2 block text-sm font-semibold">
                {t('auth.email')}
              </label>
              <Field
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder={t('auth.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-2 block text-sm font-semibold">
                {t('auth.password')}
              </label>
              <Field
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                placeholder={t('auth.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="confirm-password" className="mb-2 block text-sm font-semibold">
                {t('auth.confirmPassword')}
              </label>
              <Field
                id="confirm-password"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                placeholder={t('auth.confirmPassword')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full"
            >
              {loading ? t('auth.creating') : t('auth.createAccount')}
            </Button>
          </div>
        </form>

        <div className="text-center text-gray-600 dark:text-gray-300">
          <p>
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300">
              {t('auth.signIn')}
            </Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block">
        <SignatureArt className="aspect-square" />
        <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">{t('brand.origin')}</p>
      </div>
    </div>
  );
};

export default Signup;
